import React from 'react';
import {Phone, PhoneIncoming, PhoneMissed, PhoneOutgoing, X} from 'lucide-react';
import {CallTimelineEntry} from '../../utilis/callTimeline.ts';
import {
    callRowKind,
    CallRowKind,
    timelineCallDetail,
    timelineCallDetailText,
    timelineCallTitle,
} from '../../utilis/callRow.ts';

interface CallNotificationProps {
    entry: CallTimelineEntry;
    /** Ringing the peer back. Omitted when the room cannot be called (groups). */
    onCallBack?: () => void;
    /** Peer name for the call-back button's label. */
    peerName?: string;
    /** Group rooms print who placed the call: their name, or "You" (S10). */
    callerLabel?: string;
}

const KIND_ICONS: Record<CallRowKind, typeof Phone> = {
    'incoming-answered': PhoneIncoming,
    'outgoing-answered': PhoneOutgoing,
    'incoming-missed': PhoneMissed,
    // A call I placed that nobody took is still mine: the arrow says whose it
    // was, and the red "Not answered" says how it ended.
    'outgoing-missed': PhoneOutgoing,
    'incoming-declined': PhoneIncoming,
    'outgoing-declined': PhoneOutgoing,
    'incoming-unknown': PhoneIncoming,
    'outgoing-unknown': PhoneOutgoing,
    ringing: Phone,
};

/**
 * A call in the timeline (D-CHAT-01/S09, S10): a grey card aligned with the
 * message bubbles, the direction as its title and how it ended underneath.
 * A missed or unanswered call is marked by its red outcome, not a red card.
 */
export const CallNotification: React.FC<CallNotificationProps> = ({entry, onCallBack, peerName, callerLabel}) => {
    const kind = callRowKind(entry.direction, entry.outcome);
    const Icon = KIND_ICONS[kind] ?? Phone;
    const title = timelineCallTitle(kind);
    const detail = timelineCallDetail(kind, entry.startedAt, entry.durationMs, callerLabel);
    const lead = [detail.who, detail.outcome ? null : detail.duration].filter(Boolean).join(' · ');

    return (
        <div className="py-[7px] pl-9">
            <div
                role="group"
                data-testid="chat.message.call-notification"
                data-call-kind={kind}
                aria-label={`${title}, ${timelineCallDetailText(detail)}`}
                className="flex w-full min-w-[300px] max-w-[360px] items-center gap-3 rounded-2xl bg-surface-muted px-3 py-2.5"
            >
                {/* `!p-0`/`!text-*`: the global `button .lucide` rule pads icons
                    by 6px (shrinking them) and forces their colour. */}
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[10px] bg-surface">
                    <Icon size={16} aria-hidden="true" className="!p-0 !text-ink-secondary"/>
                </span>

                <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-semibold leading-[1.43] text-ink">{title}</div>
                    <div className="truncate text-[13px] leading-[1.35] text-ink-muted-strong">
                        {lead && <>{lead}{(detail.outcome || detail.time) && ' · '}</>}
                        {detail.outcome && (
                            <span data-testid="chat.message.call-outcome" className="text-danger-ink">
                                <X size={12} aria-hidden="true" className="!p-0 mr-0.5 inline-block align-[-1px] !text-danger-ink"/>
                                {detail.outcome}
                            </span>
                        )}
                        {detail.outcome && detail.time && ' · '}
                        {detail.time}
                    </div>
                </div>

                {onCallBack && (
                    <button
                        type="button"
                        onClick={onCallBack}
                        aria-label={peerName ? `Call back ${peerName}` : 'Call back'}
                        data-testid="chat.message.call-back"
                        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[10px] bg-surface shadow-[inset_0_0_0_1px_var(--line)] hover:bg-surface-muted"
                    >
                        <Phone size={16} aria-hidden="true" className="!p-0 !text-ink-secondary"/>
                    </button>
                )}
            </div>
        </div>
    );
};

export default CallNotification;
